import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { Card } from "../ui/Card";
import { useAuth } from "../../contexts/AuthContext";

interface Device {
  id: string;
  deviceName: string;
  deviceType: string;
  isActive: boolean;
  browser: string | { name: string; version: string };
  lastUsedAt: string;
  isTrusted: boolean;
  status?: string;
}

interface DeviceLoginTableProps {
  devices: Device[];
  onDelete?: (deviceId: string) => Promise<void> | void;
  onBlock?: (deviceId: string) => Promise<void> | void;
  onUnblock?: (deviceId: string) => Promise<void> | void;
  isLoading?: boolean;
}

export function DeviceLoginTable({
  devices,
  onDelete,
  onBlock,
  onUnblock,
  isLoading = false,
}: DeviceLoginTableProps) {
  const { hasPermission } = useAuth();

  const canBlock = hasPermission('devices:block');
  const canUnblock = hasPermission('devices:unblock');
  const canDelete = hasPermission('devices:read-all'); // Admin permission for delete

  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  const isDeviceLoading = (deviceId: string) => isLoading;

  return (
    <div className="space-y-4">
      <Card className="p-0 border shadow-sm">
        <div className="p-4 sm:p-6 pb-2">
          <h3 className="text-xl font-bold text-gray-900">Device Login</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Device
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Browser
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Last login
                </th>
                <th className="px-3 sm:px-6 py-4 w-[50px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {devices.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    No login history found.
                  </td>
                </tr>
              ) : (
                devices.map((device) => (
                  <tr
                    key={device.id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-3 sm:px-6 py-4 font-medium">
                      <div className="flex flex-col">
                        <span className="text-[#102040]">
                          {device.deviceName || 'Unknown Device'}
                        </span>
                        <span className="text-xs text-gray-400 capitalize">
                          {device.deviceType || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      {device.status?.toLowerCase() === "blocked" ||
                      !device.isActive ? (
                        <button
                          type="button"
                          disabled={isDeviceLoading(device.id)}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onUnblock?.(device.id);
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isDeviceLoading(device.id) ? 'Processing...' : 'Activate'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={isDeviceLoading(device.id)}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onBlock?.(device.id);
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isDeviceLoading(device.id) ? 'Processing...' : 'Deactivate'}
                        </button>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {typeof device.browser === "object" && device.browser?.name
                        ? `${device.browser.name} ${device.browser.version || ''}`
                        : (device.browser || 'Unknown Browser')}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {device.lastUsedAt && !isNaN(new Date(device.lastUsedAt).getTime())
                        ? new Date(device.lastUsedAt).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right relative">
                      <button
                        type="button"
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (activeMenu?.id === device.id) {
                            setActiveMenu(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setActiveMenu({
                              id: device.id,
                              position: {
                                top: rect.bottom,
                                right: window.innerWidth - rect.right,
                              },
                            });
                          }
                        }}
                      >
                        <MoreHorizontal size={20} />
                      </button>
                      <TableActionMenu
                        isOpen={activeMenu?.id === device.id}
                        onClose={() => setActiveMenu(null)}
                        position={activeMenu?.position || null}
                      >
                        {device.status?.toLowerCase() === "blocked" ? (
                        canUnblock && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onUnblock?.(device.id);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors"
                          >
                            Unblock Device
                          </button>
                        )
                      ) : (
                        canBlock && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onBlock?.(device.id);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                          >
                            Block Device
                          </button>
                        )
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          disabled={isDeviceLoading(device.id)}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDelete?.(device.id);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Delete Device
                        </button>
                      )}
                      </TableActionMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
